import type { ReactNode } from "react";
import { Code, FileText } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { StepSqlBlock } from "../StepSqlBlock";
import { StepCodeBlock } from "../StepCodeBlock";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getTransformSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const transformType = getString(config.type) || "mapping";
  const sql = getString(config.sql);
  const code = getString(config.code);
  const mappings = Array.isArray(config.mappings)
    ? (config.mappings as { source: string; dest: string }[])
    : [];

  // Type labels are technical identifiers — kept in English
  const typeLabels: Record<string, { label: string; color: string }> = {
    sql: { label: "SQL", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
    mapping: { label: "Mapping", color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
    code: { label: "JavaScript", color: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" },
    js: { label: "JavaScript", color: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" },
    javascript: { label: "JavaScript", color: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" },
  };
  const typeInfo = typeLabels[transformType] || { label: transformType, color: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" };

  items.push(
    <StepInfoRow
      key="type"
      icon={<Code className="size-3.5" />}
      label={t("summaries.transform.label_type")}
      value={
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
      }
    />
  );

  if (sql) {
    items.push(
      <StepSqlBlock key="sql" icon={<FileText className="size-3.5" />} label={t("summaries.transform.label_sql")} sql={sql} />
    );
  }

  if (code) {
    items.push(
      <StepCodeBlock key="code" icon={<Code className="size-3.5" />} label={t("summaries.transform.label_js_code")} code={code} title="JavaScript Code" />
    );
  }

  if (mappings.length > 0) {
    const maxDisplay = 3;
    const displayMappings = mappings.slice(0, maxDisplay).map((m) => `${m.source} → ${m.dest}`).join(", ");
    const remaining = mappings.length - maxDisplay;
    const mappingsValue = remaining > 0 ? `${displayMappings} (+${remaining} more)` : displayMappings;
    items.push(
      <StepInfoRow
        key="mappings"
        icon={<FileText className="size-3.5" />}
        label={t("summaries.transform.label_mappings")}
        value={<span title={mappings.map((m) => `${m.source} → ${m.dest}`).join(", ")}>{mappingsValue}</span>}
        mono
      />
    );
  }

  return items;
}
